import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useRef, useImperativeHandle, forwardRef } from "react";
import Editor from "@monaco-editor/react";
import axios from "axios";
import {
  Button,
  Menu,
  MenuItem,
  MenuDivider,
  Popover,
  Position,
  Navbar,
  NavbarGroup,
  NavbarDivider,
  Alignment,
  Icon,
  Intent,
  ContextMenu
} from "@blueprintjs/core";
import { CollaborationBadge } from "../../src/Collab/CollaborationBadge";
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { MonacoBinding, logCollabEvent } from "../../src/Collab/MonacoBinding";
import {
  parseYamlContext,
  getAvailableKeys,
  getParentKey,
  filterAvailableFiles,
  getExistingEnzymes,
  findFileWithFallback,
  extractFileMatches
} from "./monacoYamlAutoCompletionLogic";
import { matchesQuery } from "./utils/searchUtils";
import classNames from "classnames";
import { defaultEnzymesByName, aliasedEnzymesByName } from "@teselagen/sequence-utils";
const SEQUENCE_EXTENSIONS = [".gb", ".gbk", ".genbank", ".gen.gb", ".fasta", ".fa"];
const MonacoYamlEditor = forwardRef(
  ({
    filePath,
    content,
    onSave,
    onSaveAs,
    onNavigate,
    onViewProduct,
    onGenerateVerificationPrimers,
    onSaveAsPrimer,
    productExists,
    availableFiles,
    readOnly,
    theme = "vs-dark",
    additionalToolbarButtons,
    isInsidePreview,
    collabIsConnected = false,
    collabCollaborators = [],
    collabRoomName = ""
  }, ref) => {
    const editorRef = useRef(null);
    const lastSavedVersionRef = useRef(null);
    const availableFilesRef = useRef(availableFiles || []);
    const completionProviderRef = useRef(null);
    const yjsProviderRef = useRef(null);
    const ydocRef = useRef(null);
    const bindingRef = useRef(null);
    const schemaRef = useRef(null);
    const suggestionTimeoutRef = useRef(null);
    const fallbackTimerRef = useRef(null);
    const beforeUnloadHandlerRef = useRef(null);
    const [selectionInfo, setSelectionInfo] = React.useState(null);
    const [cursorPos, setCursorPos] = React.useState({ lineNumber: 1, column: 1 });
    const [isConnected, setIsConnected] = React.useState(false);
    const [collaborators, setCollaborators] = React.useState([]);
    const fetchSchema = async () => {
      if (schemaRef.current) return;
      try {
        const response = await axios.get("/api/assembly/schema");
        schemaRef.current = response.data;
      } catch (error) {
        console.error("Error fetching assembly schema:", error);
      }
    };
    React.useEffect(() => {
      fetchSchema();
    }, []);
    React.useEffect(() => {
      availableFilesRef.current = availableFiles || [];
    }, [availableFiles]);
    React.useEffect(() => {
      if (editorRef.current) {
        editorRef.current.setPosition({ lineNumber: 1, column: 1 });
        editorRef.current.revealLine(1);
      }
    }, [filePath]);
    React.useEffect(() => {
      return () => {
        if (completionProviderRef.current) {
          completionProviderRef.current.dispose();
          completionProviderRef.current = null;
        }
        if (bindingRef.current) {
          bindingRef.current.destroy();
          bindingRef.current = null;
        }
        if (yjsProviderRef.current) {
          try {
            yjsProviderRef.current.awareness?.setLocalState(null);
          } catch (e) {
          }
          yjsProviderRef.current.destroy();
          yjsProviderRef.current = null;
        }
        if (ydocRef.current) {
          ydocRef.current.destroy();
          ydocRef.current = null;
        }
        if (suggestionTimeoutRef.current) {
          clearTimeout(suggestionTimeoutRef.current);
          suggestionTimeoutRef.current = null;
        }
      };
    }, []);
    useImperativeHandle(ref, () => ({
      getIsDirty: () => {
        if (!editorRef.current) return false;
        const model = editorRef.current.getModel();
        if (!model) return false;
        const currentVersion = model.getAlternativeVersionId();
        return currentVersion !== lastSavedVersionRef.current;
      },
      getValue: () => {
        if (!editorRef.current) return "";
        return editorRef.current.getValue();
      },
      markClean: () => {
        if (!editorRef.current) return;
        lastSavedVersionRef.current = editorRef.current.getModel().getAlternativeVersionId();
      }
    }));
    const handleSaveInternal = () => {
      if (onSave && editorRef.current && !readOnly) {
        const model = editorRef.current.getModel();
        const currentVersion = model.getAlternativeVersionId();
        const isDirty = currentVersion !== lastSavedVersionRef.current;
        if (isDirty) {
          onSave(editorRef.current.getValue());
        }
      }
    };
    const handleSaveAsInternal = () => {
      if (onSaveAs && editorRef.current) {
        onSaveAs(editorRef.current.getValue());
      }
    };
    const getTargetFilenameAtPosition = (model, position) => {
      const lineContent = model.getLineContent(position.lineNumber);
      const matches = extractFileMatches(lineContent);
      for (const m of matches) {
        if (position.column >= m.startColumn && position.column <= m.endColumn) {
          return { filename: m.filename, startColumn: m.startColumn };
        }
      }
      return { filename: null, startColumn: -1 };
    };
    const getDNASequenceAtPosition = (model, position, editor) => {
      const selection = editor.getSelection();
      if (selection && !selection.isEmpty()) {
        const selectionText = model.getValueInRange(selection);
        const sequence = selectionText.replace(/\s/g, "").toUpperCase();
        if (/^[ATGCNRYKMSWBDHV]+$/i.test(sequence) && sequence.length > 5) {
          return { sequence, name: "selected_sequence" };
        }
      }
      const lineContent = model.getLineContent(position.lineNumber);
      const dnaRegex = /(?:sequence\s*:\s*)([atgcnrykmswbdhv\s]+)/i;
      const match = dnaRegex.exec(lineContent);
      if (match) {
        const sequence = match[1].replace(/\s/g, "").toUpperCase();
        if (sequence.length > 5) {
          let name = "extracted_sequence";
          const prevLine = model.getLineContent(Math.max(1, position.lineNumber - 1));
          const nameMatch = /(?:name|label)\s*:\s*([\w.-]+)/i.exec(prevLine) || /(?:name|label)\s*:\s*([\w.-]+)/i.exec(lineContent);
          if (nameMatch) name = nameMatch[1];
          return { sequence, name };
        }
      }
      return null;
    };
    const handleEditorDidMount = (editor, monaco) => {
      editorRef.current = editor;
      lastSavedVersionRef.current = editor.getModel().getAlternativeVersionId();
      const getTargetFile = (filename) => findFileWithFallback(availableFilesRef.current, filename);
      monaco.languages.registerHoverProvider("yaml", {
        provideHover: (model, position) => {
          const { filename, startColumn } = getTargetFilenameAtPosition(model, position);
          if (filename) {
            const targetFile = getTargetFile(filename);
            if (targetFile) {
              const range = new monaco.Range(
                position.lineNumber,
                startColumn,
                position.lineNumber,
                startColumn + filename.length
              );
              return {
                range,
                contents: [
                  { value: `**File Found**` },
                  { value: `Path: ${targetFile.path}` },
                  { value: `Type: ${targetFile.name.endsWith(".assembly") ? "Assembly" : "Sequence"}` },
                  { value: `[Open (Ctrl+Click)](${targetFile.path})` },
                  { value: `[Open in New Window (Ctrl+Shift+Click)](${targetFile.path})` }
                ]
              };
            } else {
              return {
                contents: [{ value: `*File not found in projects*` }]
              };
            }
          }
          return null;
        }
      });
      monaco.languages.registerLinkProvider("yaml", {
        provideLinks: (model) => {
          const links = [];
          const lines = model.getLineCount();
          for (let i = 1; i <= lines; i++) {
            const content2 = model.getLineContent(i);
            const matches = extractFileMatches(content2);
            for (const m of matches) {
              const targetFile = getTargetFile(m.filename);
              if (targetFile) {
                links.push({
                  range: new monaco.Range(i, m.startColumn, i, m.endColumn),
                  url: targetFile.path
                });
              }
            }
          }
          return { links };
        }
      });
      editor.onMouseDown((e) => {
        if (e.event.ctrlKey || e.event.metaKey) {
          const position = e.target.position;
          if (!position) return;
          const model = editor.getModel();
          const { filename } = getTargetFilenameAtPosition(model, position);
          if (filename) {
            const targetFile = getTargetFile(filename);
            if (targetFile && onNavigate) {
              const openInNewWindow = e.event.shiftKey;
              onNavigate(targetFile.path, openInNewWindow);
              e.event.preventDefault();
              e.event.stopPropagation();
            }
          }
        }
      });
      completionProviderRef.current = monaco.languages.registerCompletionItemProvider("yaml", {
        triggerCharacters: [":", " ", "-"],
        provideCompletionItems: (model, position) => {
          if (!schemaRef.current) {
            fetchSchema();
          }
          const lineContent = model.getLineContent(position.lineNumber);
          const textUntilPosition = lineContent.substring(0, position.column - 1);
          const fullText = model.getValue();
          const lines = fullText.split(/\r?\n/);
          const context = parseYamlContext(fullText, position.lineNumber);
          const keyMatch = textUntilPosition.match(/^\s*(?:-\s+)?([a-zA-Z0-9_-]+):\s*/);
          const listMatch = textUntilPosition.match(/^\s*-\s+/);
          const parentKey = getParentKey(lines, position.lineNumber, textUntilPosition, keyMatch, listMatch);
          let suggestions = [];
          if (textUntilPosition.trim() === "") {
            suggestions.push({
              label: "(newline)",
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: "\n",
              detail: "Insert a blank line",
              sortText: "00"
              // Priority at the top
            });
          }
          const trimmedText = textUntilPosition.trim();
          const isAtListDash = trimmedText === "-";
          if (trimmedText === "" || isAtListDash || !textUntilPosition.includes(":") && !textUntilPosition.includes("-")) {
            if (parentKey === "mutations" || parentKey === "mutation_item") {
              const needsDash = !isAtListDash && parentKey === "mutations";
              suggestions = [
                ...suggestions,
                {
                  label: "before",
                  kind: monaco.languages.CompletionItemKind.Field,
                  insertText: needsDash ? '- before: "$0"' : 'before: "$0"',
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: "DNA sequence to find"
                },
                {
                  label: "after",
                  kind: monaco.languages.CompletionItemKind.Field,
                  insertText: 'after: "$0"',
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: "DNA sequence to replace with"
                }
              ];
            } else if (!parentKey || parentKey === "" || parentKey === "method") {
              const availableKeys = getAvailableKeys(schemaRef.current, context.currentMethod);
              const filteredKeys = availableKeys.filter((key) => !context.usedKeys.has(key) || key === "method");
              suggestions = [
                ...suggestions,
                ...filteredKeys.map((key) => {
                  const isMutations = key === "mutations";
                  return {
                    label: key,
                    kind: monaco.languages.CompletionItemKind.Field,
                    insertText: isMutations ? 'mutations:\n  - before: "$0"' : `${key}: `,
                    insertTextRules: isMutations ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : void 0,
                    detail: schemaRef.current?.properties?.[key]?.description || "Assembly setting"
                  };
                })
              ];
            }
          }
          if (lineContent.includes("method:") && position.column > lineContent.indexOf(":") + 1) {
            const methods = schemaRef.current?.properties?.method?.enum || ["restriction", "gateway", "golden_gate", "seamless", "mutagenesis"];
            suggestions = [
              ...suggestions,
              ...methods.map((m) => ({
                label: m,
                kind: monaco.languages.CompletionItemKind.Value,
                insertText: m
              }))
            ];
          }
          if (parentKey === "enzymes") {
            const search = textUntilPosition.split(/[:\s-]/).pop().toLowerCase();
            const existingEnzymes = getExistingEnzymes(lines, position.lineNumber);
            const existingEnzymesLower = new Set(
              Array.from(existingEnzymes).map((e) => e.toLowerCase())
            );
            const enzymeNames = Array.from(
              new Set(
                Object.values(aliasedEnzymesByName || {}).flatMap(
                  (e) => e.aliases || [e.name]
                )
              )
            );
            const filteredEnzymes = enzymeNames.filter(
              (name) => name.toLowerCase().includes(search) && !existingEnzymesLower.has(name.toLowerCase())
            );
            suggestions = [
              ...suggestions,
              ...filteredEnzymes.map((name) => ({
                label: name,
                kind: monaco.languages.CompletionItemKind.Value,
                insertText: name,
                detail: "Restriction enzyme"
              }))
            ];
            return { suggestions };
          }
          const isSequenceField = parentKey === "before" || parentKey === "after" || parentKey === "target_sequence" || parentKey === "mutated_sequence" || parentKey === "template" || parentKey === "vector" || parentKey === "part";
          const isFileField = parentKey === "sequence_components" || isSequenceField || lineContent.trim().startsWith("-") && parentKey !== "mutations";
          if (isFileField) {
            const isMutationValue = parentKey === "before" || parentKey === "after";
            if (isMutationValue) {
              const quoteCount = (textUntilPosition.match(/"/g) || []).length;
              if (quoteCount % 2 !== 0) {
                return { suggestions: [] };
              }
            }
            let startCol = position.column;
            let search = "";
            const linePrefixMatch = textUntilPosition.match(/^(\s*(?:-\s+)?(?:[a-zA-Z0-9_-]+:\s*)?)(.*)$/);
            if (linePrefixMatch) {
              const prefix = linePrefixMatch[1];
              let queryPart = linePrefixMatch[2] || "";
              let quoteOffset = 0;
              if (queryPart.startsWith('"') || queryPart.startsWith("'")) {
                quoteOffset = 1;
                queryPart = queryPart.slice(1);
              }
              startCol = prefix.length + 1 + quoteOffset;
              search = queryPart;
            } else {
              const lastWord = textUntilPosition.split(/[:\s-]/).pop() || "";
              startCol = position.column - lastWord.length;
              search = lastWord;
            }
            const filteredFiles = filterAvailableFiles(availableFilesRef.current, parentKey, search, SEQUENCE_EXTENSIONS);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: startCol,
              endColumn: position.column
            };
            const searchTerms = (search || "").trim();
            const terms = searchTerms.toLowerCase().split(/\s+/).filter(Boolean);
            suggestions = [
              ...suggestions,
              ...filteredFiles.map((file) => {
                const normName = file.name.replace(/[_\/\\\\]/g, " ");
                const lowerNormName = normName.toLowerCase();
                let sortPrefix = "30";
                if (terms.length > 0) {
                  const allInName = terms.every((t) => lowerNormName.includes(t));
                  if (allInName) {
                    sortPrefix = lowerNormName.startsWith(terms[0]) ? "00" : "10";
                  } else {
                    const someInName = terms.some((t) => lowerNormName.includes(t));
                    sortPrefix = someInName ? "20" : "30";
                  }
                }
                return {
                  label: file.name,
                  kind: monaco.languages.CompletionItemKind.File,
                  insertText: file.name,
                  range,
                  sortText: `${sortPrefix}_${file.name}`,
                  filterText: searchTerms ? `${file.name} ${normName} ${searchTerms}` : file.name,
                  detail: file.path,
                  documentation: file.description || "Project file"
                };
              })
            ];
          }
          return { suggestions };
        }
      });
      const runOpen = (ed, target) => {
        const position = ed.getPosition();
        const model = ed.getModel();
        const { filename, startColumn } = getTargetFilenameAtPosition(model, position);
        if (filename && startColumn !== -1) {
          const endColumn = startColumn + filename.length;
          if (position.column >= startColumn && position.column <= endColumn) {
            const targetFile = getTargetFile(filename);
            if (targetFile && onNavigate) {
              onNavigate(targetFile.path, target);
            }
          }
        }
      };
      const runDownload = async (ed, options = {}) => {
        const position = ed.getPosition();
        const model = ed.getModel();
        const { filename, startColumn } = getTargetFilenameAtPosition(model, position);
        let targetFilePath = null;
        let targetFileName = null;
        if (filename && startColumn !== -1) {
          const endColumn = startColumn + filename.length;
          if (position.column >= startColumn && position.column <= endColumn) {
            const targetFile = getTargetFile(filename);
            if (targetFile) {
              targetFilePath = targetFile.path;
              targetFileName = targetFile.name || filename;
            } else {
              targetFilePath = filename;
              targetFileName = filename;
            }
          }
        }
        if (!targetFilePath && filePath) {
          targetFilePath = filePath;
          targetFileName = filePath.split("/").pop();
        }
        if (!targetFilePath) {
          window.toastr?.warning("No file found at cursor to download.");
          return;
        }
        try {
          const isAssembly = targetFilePath.endsWith(".assembly");
          const downloadOptions = { ...options };
          if (isAssembly && (options.format === "gb" || options.format === "fasta")) {
            downloadOptions.as_products = true;
          }
          const response = await axios.post(
            "/api/download",
            {
              paths: [targetFilePath],
              ...downloadOptions
            },
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const contentDisposition = response.headers["content-disposition"];
          let downloadName = targetFileName;
          if (options.format === "fasta") {
            downloadName = downloadName.replace(/\.(gb|gbk|genbank|gen\.gb|assembly)$/i, ".fasta");
          } else if (options.format === "gb") {
            downloadName = downloadName.replace(/\.(fasta|fa|assembly)$/i, ".gb");
          }
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(
              /filename="?([^";]+)"?/
            );
            if (fileNameMatch) {
              downloadName = fileNameMatch[1];
            }
          }
          link.setAttribute("download", downloadName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          window.toastr?.success(`Downloaded ${downloadName}`);
        } catch (error) {
          console.error("Download failed:", error);
          window.toastr?.error("Download failed: " + (error.response?.data?.error || error.message || "Unknown error"));
        }
      };
      editor.addAction({
        id: "open-file",
        label: "Open file",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1,
        run: (ed) => runOpen(ed, "_self")
      });
      editor.addAction({
        id: "open-in-tab",
        label: "Open in new tab",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.1,
        run: (ed) => runOpen(ed, "_blank")
      });
      editor.addAction({
        id: "open-in-new-window",
        label: "Open in new window",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.2,
        run: (ed) => runOpen(ed, "_window")
      });
      editor.addAction({
        id: "download-file-gb",
        label: "Download as gb",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.25,
        run: (ed) => runDownload(ed, { format: "gb" })
      });
      editor.addAction({
        id: "download-file-fasta",
        label: "Download as fasta",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.26,
        run: (ed) => runDownload(ed, { format: "fasta" })
      });
      editor.addAction({
        id: "download-file-assembly",
        label: "Download as assembly",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.27,
        run: (ed) => runDownload(ed, {})
      });
      editor.addAction({
        id: "save-as-primer-action",
        label: "Save as Primer",
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1.3,
        run: (ed) => {
          const position = ed.getPosition();
          const model = ed.getModel();
          const result = getDNASequenceAtPosition(model, position, ed);
          if (result && onSaveAsPrimer) {
            onSaveAsPrimer(result.sequence, result.name);
          } else {
            window.toastr?.warning("No DNA sequence found at cursor. Select DNA or click after 'sequence:' to save as primer.");
          }
        }
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        handleSaveInternal();
      });
      editor.onKeyDown((e) => {
        if ((e.ctrlKey || e.metaKey) && (e.keyCode === monaco.KeyCode.KeyC || e.code === "KeyC")) {
          e.stopPropagation();
        }
        if (e.keyCode === monaco.KeyCode.Enter) {
          const pos = editorRef.current.getPosition();
          const model = editorRef.current.getModel();
          if (!model || readOnly) return;
          const line = model.getLineContent(pos.lineNumber);
          const indentMatch = line.match(/^(\s*)/);
          const indent = indentMatch ? indentMatch[1] : "";
          let textToAppend = null;
          let moveCursorInside = false;
          if (/^\s*-\s+before\s*:/i.test(line)) {
            textToAppend = indent + '  after: ""';
            moveCursorInside = true;
          } else if (/^\s*after\s*:/i.test(line)) {
            textToAppend = indent.substring(0, Math.max(0, indent.length - 2)) + '- before: ""';
            moveCursorInside = true;
          } else if (/^\s*mutations\s*:/i.test(line)) {
            textToAppend = indent + '  - before: ""';
            moveCursorInside = true;
          } else if (/^\s*sequence_components\s*:/i.test(line) || /^\s*enzymes\s*:/i.test(line) || /^\s*parts\s*:/i.test(line)) {
            textToAppend = indent + "  - ";
          } else if (/^\s*-\s+/i.test(line)) {
            if (line.trim() === "-") {
              e.preventDefault();
              e.stopPropagation();
              const range = new monaco.Range(pos.lineNumber, 1, pos.lineNumber, line.length + 1);
              editor.executeEdits("list-exit", [{ range, text: indent.substring(0, Math.max(0, indent.length - 2)) }]);
              return;
            } else {
              const dashMatch = line.match(/^(\s*-\s+)/);
              textToAppend = dashMatch ? dashMatch[1] : indent + "- ";
            }
          }
          if (textToAppend) {
            e.preventDefault();
            e.stopPropagation();
            const range = new monaco.Range(pos.lineNumber, line.length + 1, pos.lineNumber, line.length + 1);
            const op = {
              range,
              text: "\n" + textToAppend,
              forceMoveMarkers: true
            };
            editor.executeEdits("yaml-autofill", [op]);
            const newLineNumber = pos.lineNumber + 1;
            const newColumn = textToAppend.length + 1;
            if (moveCursorInside) {
              const newLineContent = textToAppend;
              const quoteIndex = newLineContent.lastIndexOf('""');
              if (quoteIndex !== -1) {
                editorRef.current.setPosition({
                  lineNumber: newLineNumber,
                  column: quoteIndex + 2
                });
              } else {
                editorRef.current.setPosition({
                  lineNumber: newLineNumber,
                  column: newColumn
                });
              }
            } else {
              editorRef.current.setPosition({
                lineNumber: newLineNumber,
                column: newColumn
              });
              setTimeout(() => {
                editorRef.current?.trigger("keyboard", "editor.action.triggerSuggest", {});
              }, 50);
            }
          }
        }
      });
      const onEnterRules = [
        {
          // Rule for sequence_components: adds dash on newline
          beforeText: /^\s*sequence_components\s*:/i,
          action: {
            indentAction: monaco.languages.IndentAction.Indent,
            appendText: "- "
          }
        },
        {
          // Rule for generic list items
          beforeText: /^\s*-\s+(?!before:)/i,
          action: {
            indentAction: monaco.languages.IndentAction.None,
            appendText: "- "
          }
        }
      ];
      monaco.languages.setLanguageConfiguration("yaml", {
        onEnterRules
      });
      editor.onDidChangeModelContent((e) => {
        const changes = e.changes;
        const isNewline = changes.some((c) => c.text.includes("\n"));
        if (isNewline && !readOnly) {
          const isMutationAutofill = changes.some((c) => c.text.includes('- before: ""') || c.text.includes('after: ""'));
          if (!isMutationAutofill) {
            editor.trigger("keyboard", "editor.action.triggerSuggest", {});
          }
        }
      });
      const updateSelectionStatus = () => {
        const selection = editor.getSelection();
        const position = editor.getPosition();
        if (position) {
          setCursorPos({ lineNumber: position.lineNumber, column: position.column });
        }
        if (!selection || selection.isEmpty()) {
          setSelectionInfo(null);
          return;
        }
        const model = editor.getModel();
        if (!model) return;
        const selectedText = model.getValueInRange(selection);
        if (!selectedText) {
          setSelectionInfo(null);
          return;
        }
        const dnaOnly = selectedText.replace(/[\s_-]/g, "");
        const isDna = dnaOnly.length > 0 && /^[acgtunACGTUN]+$/.test(dnaOnly);
        let label = "";
        if (isDna) {
          if (dnaOnly.length === selectedText.length) {
            label = `Length: ${dnaOnly.length} bps`;
          } else {
            label = `Length: ${dnaOnly.length} bps (${selectedText.length} chars)`;
          }
        } else {
          const lineCount = selection.endLineNumber - selection.startLineNumber + 1;
          if (lineCount > 1) {
            label = `Length: ${selectedText.length} chars (${lineCount} lines)`;
          } else {
            label = `Length: ${selectedText.length} chars`;
          }
        }
        setSelectionInfo({
          length: isDna ? dnaOnly.length : selectedText.length,
          label,
          isDna
        });
      };
      editor.onDidChangeCursorSelection(updateSelectionStatus);
      editor.onDidChangeCursorPosition((e) => {
        if (e && e.position) {
          setCursorPos({ lineNumber: e.position.lineNumber, column: e.position.column });
        }
      });
      if (content) {
        lastSavedVersionRef.current = editor.getModel().getAlternativeVersionId();
      }
      try {
        const room = collabRoomName || filePath;
        if (room) {
          if (fallbackTimerRef.current) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
          }
          if (bindingRef.current) {
            bindingRef.current.destroy();
            bindingRef.current = null;
          }
          if (yjsProviderRef.current) {
            try {
              yjsProviderRef.current.awareness?.setLocalState(null);
            } catch (e) {
            }
            yjsProviderRef.current.destroy();
            yjsProviderRef.current = null;
          }
          if (ydocRef.current) {
            ydocRef.current.destroy();
            ydocRef.current = null;
          }
          const ydoc = new Y.Doc();
          ydocRef.current = ydoc;
          const companyId = localStorage.getItem("ove_company_id") || "default-company";
          const rawRoom = collabRoomName || filePath;
          const tenantRoom = rawRoom.includes(":") ? rawRoom : `${companyId}:${rawRoom}`;
          const token = localStorage.getItem("ove_token");
          const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
          const wsUrl = `${wsProtocol}//${window.location.host}/ws/`;
          logCollabEvent(tenantRoom, "CONNECTING_WEBSOCKET", { wsUrl, room: tenantRoom, companyId });
          const provider = new WebsocketProvider(wsUrl, tenantRoom, ydoc, {
            params: token ? { token } : {}
          });
          yjsProviderRef.current = provider;
          provider.on("status", (event) => {
            const connected = event.status === "connected";
            logCollabEvent(tenantRoom, "WEBSOCKET_STATUS", { status: event.status, connected });
            setIsConnected(connected);
          });
          const awareness = provider.awareness;
          const userColors = ["#106ba3", "#0f9960", "#d9822b", "#db3737", "#8f398f", "#00b3a4"];
          const userEmail = localStorage.getItem("ove_user_email") || "Collaborator";
          const randomColor = userColors[Math.floor(Math.random() * userColors.length)];
          awareness.setLocalStateField("user", {
            name: userEmail,
            color: randomColor
          });
          awareness.on("change", () => {
            const states = Array.from(awareness.getStates().values());
            const peers = states.filter((s) => s.user).map((s, idx) => ({
              id: `user-${idx}`,
              name: s.user.name,
              color: s.user.color
            }));
            logCollabEvent(tenantRoom, "AWARENESS_CHANGE", { peerCount: peers.length, peers });
            setCollaborators(peers);
          });
          if (beforeUnloadHandlerRef.current) {
            window.removeEventListener("beforeunload", beforeUnloadHandlerRef.current);
          }
          const handleBeforeUnload = () => {
            try {
              awareness.setLocalState(null);
            } catch (e) {
            }
          };
          beforeUnloadHandlerRef.current = handleBeforeUnload;
          window.addEventListener("beforeunload", handleBeforeUnload);
          const ytext = ydoc.getText("content");
          const model = editor.getModel();
          const bindEditor = () => {
            if (bindingRef.current) return;
            const currentYText = ytext.toString();
            const currentModelVal = model ? model.getValue() : "";
            logCollabEvent(room, "INIT_BINDING_SYNC_CHECK", {
              ytextLen: currentYText.length,
              modelLen: currentModelVal.length
            });
            if (currentYText.length === 0 && currentModelVal.length > 0) {
              ydoc.transact(() => {
                if (ytext.length === 0) {
                  ytext.insert(0, currentModelVal);
                }
              }, "initial-model-content");
              logCollabEvent(room, "INITIALIZED_YTEXT_FROM_MODEL", { len: currentModelVal.length });
            } else if (currentYText.length > 0 && currentModelVal !== currentYText) {
              if (model) {
                model.setValue(currentYText);
                lastSavedVersionRef.current = model.getAlternativeVersionId();
              }
              logCollabEvent(room, "INITIALIZED_MODEL_FROM_YTEXT", { len: currentYText.length });
            }
            const binding = new MonacoBinding(
              ytext,
              model,
              /* @__PURE__ */ new Set([editor]),
              awareness,
              monaco,
              room,
              (remoteModel) => {
                if (remoteModel) {
                  lastSavedVersionRef.current = remoteModel.getAlternativeVersionId();
                }
              }
            );
            bindingRef.current = binding;
          };
          provider.on("sync", (isSynced) => {
            logCollabEvent(room, "PROVIDER_SYNC_EVENT", {
              isSynced,
              ytextLen: ydoc.getText("content").toString().length
            });
            if (isSynced) {
              bindEditor();
            }
          });
          fallbackTimerRef.current = setTimeout(() => {
            if (!bindingRef.current) {
              logCollabEvent(room, "SYNC_FALLBACK_TRIGGERED");
              bindEditor();
            }
          }, 1200);
        }
      } catch (err) {
        console.warn("Failed to initialize Yjs Monaco collaboration:", err);
        logCollabEvent(collabRoomName || filePath, "COLLAB_INIT_ERROR", { error: String(err) });
      }
    };
    React.useEffect(() => {
      if (editorRef.current) {
        editorRef.current.updateOptions({ readOnly });
      }
    }, [readOnly]);
    React.useEffect(() => {
      return () => {
        if (fallbackTimerRef.current) {
          clearTimeout(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
        if (beforeUnloadHandlerRef.current) {
          window.removeEventListener("beforeunload", beforeUnloadHandlerRef.current);
          beforeUnloadHandlerRef.current = null;
        }
        if (bindingRef.current) {
          bindingRef.current.destroy();
          bindingRef.current = null;
        }
        if (yjsProviderRef.current) {
          try {
            yjsProviderRef.current.awareness?.setLocalState(null);
          } catch (e) {
          }
          yjsProviderRef.current.destroy();
          yjsProviderRef.current = null;
        }
        if (ydocRef.current) {
          ydocRef.current.destroy();
          ydocRef.current = null;
        }
      };
    }, []);
    React.useEffect(() => {
      if (bindingRef.current || yjsProviderRef.current) {
        logCollabEvent(collabRoomName || filePath, "IGNORING_PROP_CONTENT_UPDATE_DUE_TO_COLLAB", {
          propContentLen: content ? content.length : 0,
          currentModelLen: editorRef.current?.getModel()?.getValue()?.length
        });
        return;
      }
      if (editorRef.current) {
        const model = editorRef.current.getModel();
        if (model) {
          if (model.getValue() !== content) {
            model.setValue(content || "");
          }
          const currentVersion = model.getAlternativeVersionId();
          lastSavedVersionRef.current = currentVersion;
        }
      }
    }, [content]);
    const fileMenu = /* @__PURE__ */ jsxs(Menu, { children: [
      /* @__PURE__ */ jsx(
        MenuItem,
        {
          icon: "floppy-disk",
          text: "Save",
          labelElement: /* @__PURE__ */ jsx(Icon, { icon: "key-command" }),
          onClick: handleSaveInternal,
          disabled: readOnly
        }
      ),
      /* @__PURE__ */ jsx(
        MenuItem,
        {
          icon: "floppy-disk",
          text: "Save As...",
          onClick: handleSaveAsInternal,
          disabled: readOnly
        }
      )
    ] });
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: "ove-yaml-monaco-wrapper",
        onKeyDownCapture: (e) => {
          if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "c" || e.code === "KeyC")) {
            e.stopPropagation();
          }
        },
        onKeyDown: (e) => {
          if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "c" || e.code === "KeyC")) {
            e.stopPropagation();
          }
        },
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
          backgroundColor: "#f5f8fa"
        },
        children: [
          !isInsidePreview && /* @__PURE__ */ jsx("div", { className: "ove-yaml-header", children: /* @__PURE__ */ jsx(Navbar, { style: { height: 30, padding: "0 10px", boxShadow: "none" }, children: /* @__PURE__ */ jsxs(NavbarGroup, { align: Alignment.LEFT, style: { height: 30 }, children: [
            /* @__PURE__ */ jsx(Popover, { content: fileMenu, position: Position.BOTTOM_LEFT, children: /* @__PURE__ */ jsx(Button, { minimal: true, small: true, text: "File" }) }),
            /* @__PURE__ */ jsx(Button, { minimal: true, small: true, text: "Edit", disabled: true }),
            /* @__PURE__ */ jsx(Button, { minimal: true, small: true, text: "View", disabled: true }),
            /* @__PURE__ */ jsx(Button, { minimal: true, small: true, text: "Tools", disabled: true }),
            /* @__PURE__ */ jsx(
              Button,
              {
                minimal: true,
                small: true,
                text: "Help",
                onClick: () => {
                  window.open("/docs/ASSEMBLY_GUIDE.md", "_blank");
                }
              }
            ),
            onViewProduct && /* @__PURE__ */ jsx(
              Button,
              {
                minimal: true,
                small: true,
                text: "View Product",
                disabled: !productExists,
                onClick: onViewProduct,
                title: productExists ? "View the generated GenBank file" : "Product not found. Save the assembly to generate it."
              }
            ),
            onGenerateVerificationPrimers && /* @__PURE__ */ jsx(
              Button,
              {
                minimal: true,
                small: true,
                text: "Generate Verification Primers",
                disabled: !productExists,
                onClick: onGenerateVerificationPrimers,
                intent: Intent.PRIMARY,
                title: productExists ? "Generate a report for verification primers" : "Product not found. Save the assembly to generate it."
              }
            )
          ] }) }) }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "ove-yaml-toolbar",
              style: {
                height: 40,
                padding: "5px 10px",
                display: "flex",
                alignItems: "center",
                borderTop: "1px solid #ddd",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#f5f8fa"
              },
              children: [
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    minimal: true,
                    icon: "floppy-disk",
                    onClick: handleSaveInternal,
                    disabled: readOnly,
                    title: "Save",
                    style: { color: "#106ba3" }
                  }
                ),
                additionalToolbarButtons,
                /* @__PURE__ */ jsx(NavbarDivider, {}),
                /* @__PURE__ */ jsx(Icon, { icon: "style", style: { marginLeft: 10, color: "#5c7080" } }),
                /* @__PURE__ */ jsx(
                  CollaborationBadge,
                  {
                    isConnected: isConnected || collabIsConnected,
                    collaborators: collaborators.length > 0 ? collaborators : collabCollaborators,
                    roomName: collabRoomName || filePath
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { flexGrow: 1, overflow: "hidden" }, children: /* @__PURE__ */ jsx(
            Editor,
            {
              height: "100%",
              defaultLanguage: "yaml",
              value: content,
              theme,
              onMount: handleEditorDidMount,
              options: {
                readOnly,
                minimap: { enabled: true },
                scrollBeyondLastLine: false,
                wordWrap: "on",
                automaticLayout: true,
                tabSize: 2,
                insertSpaces: true,
                formatOnType: true,
                quickSuggestions: {
                  other: true,
                  queries: true,
                  strings: true
                }
              }
            }
          ) }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "veStatusBar ove-monaco-status-bar",
              style: {
                display: "flex",
                alignItems: "center",
                fontSize: "13px",
                minHeight: "26px",
                maxHeight: "26px",
                padding: "0 12px",
                background: "#ebf1f5",
                borderTop: "1px solid #ddd",
                color: "#5c7080",
                userSelect: "none"
              },
              children: [
                /* @__PURE__ */ jsx("div", { className: "veStatusBarItem", style: { display: "flex", alignItems: "center", minWidth: "fit-content" }, children: `Ln ${cursorPos.lineNumber}, Col ${cursorPos.column}` }),
                /* @__PURE__ */ jsx("div", { className: "veStatusBarSpacer", style: { height: "14px", width: "1px", background: "lightgrey", margin: "0 8px" } }),
                selectionInfo ? /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "veStatusBarItem",
                      "data-test": "monaco-selection-length",
                      style: {
                        display: "flex",
                        alignItems: "center",
                        minWidth: "fit-content",
                        fontWeight: 500,
                        color: "#106ba3"
                      },
                      children: selectionInfo.label
                    }
                  ),
                  /* @__PURE__ */ jsx("div", { className: "veStatusBarSpacer", style: { height: "14px", width: "1px", background: "lightgrey", margin: "0 8px" } })
                ] }) : null,
                /* @__PURE__ */ jsx("div", { style: { marginLeft: "auto", fontSize: "12px", color: "#8a9ba8" }, children: "Assembly / YAML" })
              ]
            }
          )
        ]
      }
    );
  }
);
export default MonacoYamlEditor;
